import {
  Link,
  createFileRoute,
  stripSearchParams,
  // useRouter,
} from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { CheckIcon, ChevronDownIcon, ChevronRight } from "lucide-react";
import { z } from "zod/v4";
import { CommandItem } from "cmdk";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PopoverClose } from "@radix-ui/react-popover";
import type { ColumnDef } from "@tanstack/react-table";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import type { HistoryListEntry } from "@/lib/queries/detector/history";
import type { FileRoutesByTo } from "@/routeTree.gen";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { CoinIcon } from "@/components/icons/coin";
import { cn } from "@/lib/utils";
import { historyListQueryOptions } from "@/lib/queries/detector/history";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput } from "@/components/ui/command";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AllServices } from "@/lib/types/history";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";

const historySearchSchema = z.object({
  page: z.number().int().positive().default(1),
  page_size: z.number().int().positive().max(20).default(20),
  selectedServices: z.array(z.enum(AllServices)).optional(),
});

export const Route = createFileRoute("/_app/history")({
  component: RouteComponent,
  validateSearch: historySearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        selectedServices: [],
      }),
    ],
  },
  loaderDeps: ({ search: { page, page_size, selectedServices } }) => ({
    page,
    page_size,
    selectedServices,
  }),
  beforeLoad: () => ({
    isProblematicScrollPage: true,
  }),

  loader({
    context: { queryClient },
    deps: { page, page_size, selectedServices },
  }) {
    void queryClient.ensureQueryData(
      historyListQueryOptions(
        {
          page,
          page_size,
        },
        selectedServices,
      ),
    );
  },
  staticData: {
    title: "History (BETA)",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "View your complete activity history across all tools and services. Easily review past analyses and track usage by date and product.",
      },
    ],
  }),
});

function RouteComponent() {
  return (
    <div className="w-full p-4 md:p-8 dark:border-gray-800 dark:bg-black dark:text-white">
      <div className="w-full">
        <div className="min-h-screen w-full rounded-lg border border-gray-200 p-6 shadow-sm dark:border-gray-800 dark:bg-black dark:text-white">
          <div className="p-6">
            <div className="flex items-center justify-between max-md:flex-col max-md:place-items-start">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  History [BETA]
                </h1>
                <p className="text-sm text-gray-500 dark:text-white">
                  See all data privacy, provenance and content generation
                  operations.
                </p>
              </div>
              <div>
                <div className="mb-4 flex items-center justify-end gap-2 max-md:mt-2 max-md:flex-row">
                  {/* {selectedItems.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex items-center gap-2 dark:bg-pink-500"
                    >
                      <Trash2 className="h-4 w-4" />
                      <DeleteHistoryButton />
                      {selectedItems.length > 1
                        ? `(${selectedItems.length})`
                        : ""}
                    </Button>
                  )} */}
                  <ServiceFilterCombobox />
                </div>
              </div>
            </div>
          </div>

          <HistoryTable />
        </div>
      </div>
    </div>
  );
}

const modelMetaMap = {
  AI_DETECTION_HIGHLIGHTER: {
    name: "AI Detector",
    to: "/ai-detector",
  },
  PLAGIARISM: {
    name: "Copyright Detector",
    to: "/copyright-detector",
  },
  QUILLBOT_ANALYSIS: {
    name: "Humanized Text Detector",
    to: "/humanized-text-detector",
  },
  WRITING_ANALYSIS: {
    name: "Writing Analysis",
    to: "/writing-analysis",
  },
  HUMANIZER: {
    name: "AI Humanizer",
    to: "/content-humanizer",
  },

  SOURCE_DETECTION: {
    name: "AI Source Detection (Legacy)",
  },
  AI_DETECTION: {
    name: "AI Detection (Legacy)",
  },

  AI_WRITER: {
    name: "AI Writer",
  },
  REWRITE: {
    name: "Rewrite",
  },
  ADD_CONTENT: {
    name: "Add Content",
  },
  SUMMARIZE: {
    name: "Summarize",
  },
  EXPAND: {
    name: "Expand",
  },
  SHORTEN: {
    name: "Shorten",
  },

  PII_TEXT_DETECT: {
    name: "PII Detector",
  },

  VEXON: {
    name: "Vexon Voice Detector",
  },
} as const satisfies Record<
  AllServices,
  {
    name: string;
    to?: keyof FileRoutesByTo;
  }
>;

function ServiceFilterCombobox() {
  const selectedServices = Route.useSearch({
    select: (s) => s.selectedServices,
  });

  const [open, setOpen] = useState(false);

  const navigate = Route.useNavigate();
  const setSelectedServices = useCallback<
    Dispatch<SetStateAction<typeof selectedServices>>
  >(
    (newVal) => {
      const val: typeof selectedServices =
        typeof newVal === "function" ? newVal(selectedServices) : newVal;

      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          selectedServices: val,
          page: 1,
        }),
        viewTransition: false,
      });
    },
    [selectedServices, navigate],
  );

  const allSelected = selectedServices === undefined;

  const selectItem = useCallback(
    (item: AllServices) => {
      setSelectedServices((old) => {
        const svcs = old?.slice() ?? [];

        const found = svcs.indexOf(item);

        if (found > -1) svcs.splice(found, 1);
        else svcs.push(item);

        return svcs;
      });
    },
    [setSelectedServices, allSelected],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          // className="w-[200px] justify-between"
          className="flex items-center gap-2"
        >
          Filter Services
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="px-2">
        <Command>
          <CommandInput placeholder="Search service..." />
          <CommandGroup>
            <CommandItem onSelect={() => setSelectedServices([])}>
              Deselect All
            </CommandItem>
          </CommandGroup>
          <CommandGroup>
            {Object.entries(modelMetaMap).map(([svcName, svc]) => (
              <CommandItem
                key={svcName}
                value={svcName}
                keywords={[svc.name]}
                onSelect={(v) => selectItem(v as keyof typeof modelMetaMap)}
                className="my-2 flex w-full items-center gap-2 rounded-sm px-2 outline-1 dark:text-white"
              >
                <>
                  {svc.name}
                  {selectedServices?.includes(svcName) && (
                    <CheckIcon className="ml-auto justify-self-end" />
                  )}
                </>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const columns: Array<ColumnDef<HistoryListEntry>> = [
  {
    id: "modelName",
    header: "Model",
    accessorKey: "model_name",
    cell: (ctx) => {
      const m = ctx.getValue<HistoryListEntry["model_name"]>();
      return m in modelMetaMap ? modelMetaMap[m].name : `Unkown model: ${m}`;
    },
  },
  {
    header: "Tokens Used",
    accessorKey: "tokens_used",
    cell: (ctx) => (
      <span className="self-center">
        {ctx.getValue<number>()} <CoinIcon className="inline" />
      </span>
    ),
    size: 60,
  },
  {
    header: "Date",
    accessorKey: "created_at",
    cell: (ctx) => (
      <span>{new Date(ctx.getValue<string>()).toLocaleString()}</span>
    ),
  },
  {
    id: "navigate",
    header: () => <></>,
    cell(ctx) {
      const modelName =
        ctx.row.getValue<HistoryListEntry["model_name"]>("modelName");
      if ("to" in modelMetaMap[modelName])
        return (
          <div className="flex flex-row justify-end pr-1">
            <ChevronRight />
          </div>
        );
      else return null;
    },
    size: 50,
  },
];

function HistoryTable() {
  const { page, page_size, selectedServices } = Route.useSearch();

  const history = useQuery({
    ...historyListQueryOptions(
      {
        page,
        page_size,
      },
      selectedServices,
    ),
    placeholderData: keepPreviousData,
  });

  const table = useReactTable({
    columns,
    getCoreRowModel: getCoreRowModel(),
    data: history.data?.items ?? [],
    rowCount: history.data?.count ?? 0,
    state: {
      pagination: {
        pageIndex: page,
        pageSize: page_size,
      },
    },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,

    columnResizeMode: "onChange",
    enableColumnResizing: true,
  });

  const maxPage = Math.ceil((history.data?.count ?? 1) / page_size);

  const firstPages = [1, 2, 3].slice(0, page - 1);
  const lastPages = [maxPage - 2, maxPage - 1, maxPage].slice(
    Math.max(3 - (maxPage - page), 0),
    undefined,
  );

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-gray-50 text-sm text-gray-500 dark:bg-transparent dark:text-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={
                        {
                          "--custom-width": `${header.getSize()}px`,
                        } as CSSProperties
                      }
                      className="group/head relative w-(--custom-width) border-gray-400 text-sm"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onDoubleClick={header.column.resetSize}
                          data-resizing={
                            header.column.getIsResizing() ? "true" : undefined
                          }
                          className="absolute top-0 right-0 h-full w-0.5 cursor-col-resize touch-none bg-black/5 opacity-0 select-none group-hover/head:opacity-100 data-[resizing]:bg-blue-300"
                          // className={`resizer ${
                          //   header.column.getIsResizing() ? "isResizing" : ""
                          // }`}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="divide-y divide-gray-200">
            {history.isLoading || history.isPlaceholderData ? (
              [...Array(page_size)].map((_, i) => (
                <TableRow key={i} className="h-11">
                  <TableCell colSpan={columns.length} className="w-full">
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => {
                const modelName = row.getValue<AllServices>("modelName");
                const model = modelMetaMap[modelName];

                const rowContents = row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={
                      {
                        "--custom-width": `${cell.column.getSize()}px`,
                      } as CSSProperties
                    }
                    className="w-(--custom-width)"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ));
                if ("to" in model) {
                  return (
                    <Link
                      data-slot="table-row"
                      className={cn(
                        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
                        "table-row align-[inherit]",
                        "h-11",
                      )}
                      to={model.to}
                      search={{ history_id: row.original.history_id }}
                    >
                      {rowContents}
                    </Link>
                  );
                } else
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="h-11"
                    >
                      {rowContents}
                    </TableRow>
                  );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                disabled={history.data?.previous_page == null || page == 1}
                className={cn(
                  history.data?.previous_page == null && "invisible",
                )}
                from={Route.fullPath}
                to="."
                search={(prev) => ({
                  ...prev,
                  page: prev.page - 1,
                })}
              />
            </PaginationItem>
            <div className="flex w-85 flex-row justify-center">
              {firstPages.map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink to="." search={(prev) => ({ ...prev, page })}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {firstPages.at(-1)! < page - 1 && (
                <PageSelectEllipsis pageCount={table.getPageCount()} />
              )}
              <PaginationItem key={page}>
                <PaginationLink
                  isActive
                  disabled
                  to="."
                  search={(prev) => ({ ...prev, page })}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
              {lastPages.at(0)! > page + 1 && (
                <PaginationItem>
                  <PageSelectEllipsis pageCount={table.getPageCount()} />
                </PaginationItem>
              )}
              {lastPages.map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink to="." search={(prev) => ({ ...prev, page })}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
            </div>
            <PaginationItem>
              <PaginationNext
                disabled={history.data?.next_page == null || page == maxPage}
                className={cn(history.data?.next_page == null && "invisible")}
                from={Route.fullPath}
                to="."
                search={(prev) => ({
                  ...prev,
                  page: prev.page + 1,
                })}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </>
  );
}

function PageSelectEllipsis({ pageCount }: { pageCount: number }) {
  const navigate = Route.useNavigate();
  const { page: currentPage } = Route.useSearch();
  const [page, setPage] = useState(currentPage);
  const [open, setOpen] = useState(false);

  const submit = () =>
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, page }),
    });

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger>
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
      </PopoverTrigger>
      <PopoverContent className="flex w-fit flex-row gap-2 p-2">
        <Input
          className="w-15"
          type="number"
          min={1}
          max={pageCount}
          value={page}
          onChange={(e) => setPage(parseInt(e.target.value))}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              setOpen(false);
              submit();
            }
          }}
        />
        <PopoverClose asChild>
          <Button size="icon" onClick={submit}>
            <CheckIcon />
          </Button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
}
